/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { IFastWaveConfig, IJob } from '../../model/IFastWaveConfig';
import { IState } from '../../reducers';
import { JOBS_IN_PROGRESS } from '../../model/IJobStatus';
import { getJob } from '../../helpers/fastwave';
import { JOBS } from '../../shared/constants';
import { subTitleStyle, titleStyle } from '../../shared/styles';
import { useIntl } from 'react-intl';
import RightCompress from '../../icons/RightCompress.svg?react';
import Left from '../../icons/Left.svg?react';
import MikeButton from '../mike-button';
import { createContainer } from '../../actions/job';
import mikeSharedTheme from '../../styles/mikeSharedTheme';
import { ITestRunFolder } from '../../reducers/projectContent';
import { StyledSelect } from '../Sidebar/StyledSelect';
import { setSelectedTestRunFolder, showTestRunOnMap } from '../../actions/projectContent';
import { ILayerGroup, MODEL_RUN_GROUP_ID } from '../../reducers/legend';
import { selectStyle } from '../../styles/sharedDropdownStyles';
import { useMatch } from 'react-router-dom';
import DataTable from '../TableComponents';
import MikeStickyPanel from '../mike-sticky-panel';
import { MikeStickyPanelHeaderContainer } from '../mike-sticky-panel/MikeStickyPanelHeaderContainer';
import MikeStickyPanelContent from '../mike-sticky-panel/MikeStickyPanelContent';

const titleContainer = css`
  background-color: ${mikeSharedTheme.palette.mediumGrey?.main};
  padding-bottom: ${mikeSharedTheme.spacing(1)};
  padding-top: ${mikeSharedTheme.spacing(1)};
  display: flex;
  justify-content: space-between;
`

const popupStyle = css` 
  z-index: 10;
  position: absolute;
  width: calc(100% - 800px);
  height: calc(100% - 60px);
  left: 800px;
  background-color: ${mikeSharedTheme.palette.lightGrey.main};
`;

const collapseContainerStyle = css`
    z-index: 11;
    position: absolute;
    width: 40px;
    height: 40px;
    top: 50%;
    right: 0;
    background-color: white;
    display: flex;
    flex-direction: column;
    justify-content: center; 
`;

const paddingStyle = css`
  padding: ${mikeSharedTheme.spacing(2)};
`;

const noTestRunMessageStyle = css`
  padding-bottom: ${mikeSharedTheme.spacing(1)};
`

const noTestRunTableStyle = css`
  height: calc(100% - 100px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const collapseButtonStyle = css`
  width: 40px;
  min-width: 40px;
  height: 40px;
  &.MuiIconButton-root {
    padding: 0;
  }
`
const linkButtonStyle = css`
  &.MuiButton-root {   
    padding-bottom: 0;  
    padding-top: 2px;  
    padding-left: 2px; 
    padding-right: 2px; 
    height: 1.286rem;
    white-space: nowrap;
    &:disabled {
      height: 1.286rem;
    };
  }
`;

const textAndlinkButtonContainerStyle = css`
  display: flex;
  align-items: center;
`
const previousTestRunContainerStyle = css`  
  padding-right: ${mikeSharedTheme.spacing(2)}; 
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${mikeSharedTheme.palette.lightGrey?.main}; 
`
const buttonsContainerStyle= css`
  display: flex;
  align-items: center;
`
const buttonContainerStyle = css`
  padding-right: ${mikeSharedTheme.spacing(2)};
`
const previousTestRunSelectStyle = css`  
  padding-right: ${mikeSharedTheme.spacing(2)};
  width: ${mikeSharedTheme.spacing(27.5)};
`
const VALUEFIELD = "id";
const PRIMARYFIELD = "name";

const TestRunTable = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const matchSetup = useMatch('/project/:id/setup'); 
  
  const testRunOutput = useSelector(
    (state: IState) => state.mapContent.testRunOutput
  );  

  const loadingTestRunOutput = useSelector(
    (state: IState) => state.mapContent.loadingTestRunOutput
  ); 

  const jobs: Array<IJob> = useSelector(
    (state: IState) => state.job.jobs
  ); 
  
  const loadingTestRunFolders: boolean = useSelector(
    (state: IState) => state.projectContent.loadingTestRunFolders
  ); 

  const testRunFolders: Array<ITestRunFolder> = useSelector(
    (state: IState) => state.projectContent.testRunFolders
  ); 

  const selectedTestRunFolder: ITestRunFolder = useSelector(
    (state: IState) => state.projectContent.selectedTestRunFolder
  ); 

  const loadingTestRunMapLayer: boolean = useSelector(
    (state: IState) => state.projectContent.loadingTestRunMapLayer
  ); 

  const jobIsRunning: boolean = useMemo(() => {
    const job = getJob(jobs, JOBS.TESTSETUP);
    const status = job !== undefined && job.status ? job.status : "";
    return status && JOBS_IN_PROGRESS.includes(status.toLowerCase()) ? true : false; 
  }, [jobs])

  const config: IFastWaveConfig = useSelector(
    (state: IState) => state.mapContent.fastWaveConfig
  ); 

  const layerGroups = useSelector(
    (state: IState) => state.legend.layerGroups
  ); 

  const hasModelRunLayer = useMemo(() => {
    if (layerGroups && layerGroups.length > 0){
      const ids = layerGroups.map((lg: ILayerGroup) => lg.id)
      return ids.includes(MODEL_RUN_GROUP_ID)
    }
    return false
  }, [layerGroups])
  
  const readyForTestModelRun: boolean = useMemo(() => {
    return config && config.test_event_time && config.setup ? true : false
  }, [config])

  const showTable = testRunOutput && testRunOutput.columns && testRunOutput.rows

  const [showMap, setShowMap] = useState(false) 

  const toggleCollapse = () => {
    setShowMap((previous) => {return !previous})
  };

  const handleRunTest = () => {   
    dispatch(createContainer(JOBS.TESTSETUP))
  }
  
  const handleTestRunSelected = useCallback((id: string) => {  
    const testRunFolder = testRunFolders.find(((tr: ITestRunFolder) => tr.id === id))
    dispatch(setSelectedTestRunFolder(testRunFolder))
  }, [dispatch, testRunFolders])

  const handleShowTestRunOnMap = useCallback(() => { 
    dispatch(showTestRunOnMap(selectedTestRunFolder))
  }, [dispatch, selectedTestRunFolder])
 
  return (   
    <>
      <div css={collapseContainerStyle}>    
        <IconButton onClick={toggleCollapse} css={collapseButtonStyle}>
          {showMap ? <Left/> : <RightCompress/>}
        </IconButton>      
     </div> 
   {!showMap && 
    <div css={popupStyle}>
      <div css={titleContainer} >
        <div>
          <Typography variant="h5" css={titleStyle}>{intl.formatMessage({id: 'testRunTable.tableViewTitle'})}</Typography>
          <Typography variant="body2" css={subTitleStyle}>{intl.formatMessage({id: 'testRunTable.tableViewSubTitle'})}</Typography>
        </div>
        <div css={previousTestRunSelectStyle}>
            <Typography variant="body2">{intl.formatMessage({id: 'testRunTable.selectPrevious'})}</Typography>
            <StyledSelect             
              fullWidth={true}
              name={selectedTestRunFolder ? selectedTestRunFolder.name : ''}
              value={selectedTestRunFolder ? selectedTestRunFolder.id : ''}         
              valueField={VALUEFIELD}
              primaryField={PRIMARYFIELD}
              items={testRunFolders}
              onChange={(event: React.ChangeEvent<any>) => handleTestRunSelected(event.target.value)}
              disabled={loadingTestRunFolders || testRunFolders.length === 0}
              showColorIcon={false}
              addErrorPlaceHolder={false}
              selectSX={selectStyle}
            />
          </div>       
      </div>
      
        {!showTable ? matchSetup ?
          <div css={noTestRunTableStyle}>            
            <Typography css={noTestRunMessageStyle} variant="body2">{intl.formatMessage({id: 'testRunTable.noTestRuns'})}</Typography>
            <Typography css={noTestRunMessageStyle} variant="body2">{intl.formatMessage({id: 'testRunTable.clickOnANode'})}</Typography>
            <div css={textAndlinkButtonContainerStyle}>
              <Typography variant="body2">{intl.formatMessage({id: 'testRunTable.toSelect'})}</Typography>
              <MikeButton css={linkButtonStyle} variant="text" onClick={handleRunTest} disabled={!readyForTestModelRun || jobIsRunning}>
                {intl.formatMessage({id: 'testRunTable.testRun'})}               
              </MikeButton>                   
            </div>          
          </div> : 
          <div css={noTestRunTableStyle}>            
            <Typography css={noTestRunMessageStyle} variant="body2">{intl.formatMessage({id: 'results.missingData.title'})}</Typography>
            <Typography css={noTestRunMessageStyle} variant="body2">{intl.formatMessage({id: 'results.missingData.subTitle'})}</Typography>
          </div>
        :       
           <MikeStickyPanel>
            <MikeStickyPanelHeaderContainer>
              <div css={previousTestRunContainerStyle}>        
                <Typography css={paddingStyle} variant="h5">{intl.formatMessage({id: 'testRunTable.resultOfTestRun'})}</Typography>
                <div css={buttonsContainerStyle}>
                  <div css={buttonContainerStyle}>
                    <MikeButton css={linkButtonStyle} variant="text" onClick={handleShowTestRunOnMap} active={loadingTestRunMapLayer}  disabled={!selectedTestRunFolder}>
                        {intl.formatMessage({id: 'testRunTable.createModelRunLayer'})}               
                    </MikeButton> 
                  </div>              
                  <MikeButton css={linkButtonStyle} variant="text" onClick={() => setShowMap(true)} disabled={!hasModelRunLayer || loadingTestRunMapLayer}>
                      {intl.formatMessage({id: 'testRunTable.showTestRunOnMap'})}               
                  </MikeButton> 
                </div> 
              </div>  
            </MikeStickyPanelHeaderContainer>
            <MikeStickyPanelContent>
              <DataTable              
                columns={testRunOutput.columns}
                notSortableColumns={testRunOutput.columns.map((column) => column.field) as []}
                data={testRunOutput.rows}
                idField={testRunOutput.columns[0].field}
                onCellClick={() => {}}
                onSelectionChange={(selectedItems: Array<any>) => {console.log(selectedItems)}}
                order={'asc'}
                orderBy={testRunOutput.columns[0].field}
                selectable={false}
                loading={jobIsRunning || loadingTestRunOutput}
                selectedRows={new Array<any>()}
              /> 
      
            </MikeStickyPanelContent>
          </MikeStickyPanel>
          }
        </div>
      }
    </>
  )
};

export default TestRunTable;